"use client";

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuthenticatedFetch } from "@/hooks/useAuthenticatedFetch";
import { Timeframe, IndicatorGroupDTO, IndicatorSettingDTO } from "@lacon/ws";

// Ordered timeframes for display
const ORDERED_TIMEFRAMES: Timeframe[] = [
  Timeframe["1m"],
  Timeframe["5m"],
  Timeframe["15m"],
  Timeframe["30m"],
  Timeframe["1h"],
  Timeframe["4h"],
  Timeframe["6h"],
  Timeframe["1d"],
  Timeframe["1w"],
];

export function IndicatorSettingsPage() {
  const { isAdmin, loading: roleLoading, isSignedIn } = useUserRole();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const [indicators, setIndicators] = useState<IndicatorGroupDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingStates, setSavingStates] = useState<Map<string, boolean>>(
    new Map()
  );

  // Redirect if not admin
  useEffect(() => {
    if (!roleLoading && !isSignedIn) {
      navigate("/", { replace: true });
    }
    if (!roleLoading && isSignedIn && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [roleLoading, isAdmin, isSignedIn, navigate]);

  // Fetch indicator settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data =
        await authenticatedFetch<IndicatorGroupDTO[]>("/admin/indicators");
      setIndicators(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    if (isAdmin) {
      void fetchSettings();
    }
  }, [isAdmin, fetchSettings]);

  // Toggle a single setting
  const toggleSetting = async (
    indicatorId: string,
    timeframe: Timeframe,
    currentEnabled: boolean
  ) => {
    const key = `${indicatorId}:${timeframe}`;
    setSavingStates((prev) => new Map(prev).set(key, true));

    try {
      await authenticatedFetch(
        `/admin/indicators/${indicatorId}/${timeframe}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled: !currentEnabled }),
        }
      );

      // Update local state
      setIndicators((prev) =>
        prev.map((ind) =>
          ind.indicatorId === indicatorId
            ? {
                ...ind,
                settings: ind.settings.map((s) =>
                  s.timeframe === timeframe
                    ? { ...s, enabled: !currentEnabled }
                    : s
                ),
              }
            : ind
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update setting");
    } finally {
      setSavingStates((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Enable all timeframes for an indicator
  const enableAll = async (indicatorId: string) => {
    const key = `${indicatorId}:all`;
    setSavingStates((prev) => new Map(prev).set(key, true));

    try {
      await authenticatedFetch(`/admin/indicators/${indicatorId}/enable-all`, {
        method: "POST",
      });

      setIndicators((prev) =>
        prev.map((ind) =>
          ind.indicatorId === indicatorId
            ? {
                ...ind,
                settings: ind.settings.map((s) => ({ ...s, enabled: true })),
              }
            : ind
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enable all");
    } finally {
      setSavingStates((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Disable all timeframes for an indicator
  const disableAll = async (indicatorId: string) => {
    const key = `${indicatorId}:all`;
    setSavingStates((prev) => new Map(prev).set(key, true));

    try {
      await authenticatedFetch(`/admin/indicators/${indicatorId}/disable-all`, {
        method: "POST",
      });

      setIndicators((prev) =>
        prev.map((ind) =>
          ind.indicatorId === indicatorId
            ? {
                ...ind,
                settings: ind.settings.map((s) => ({ ...s, enabled: false })),
              }
            : ind
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disable all");
    } finally {
      setSavingStates((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Get setting for a specific timeframe
  const getSetting = (
    settings: IndicatorSettingDTO[],
    timeframe: Timeframe
  ): IndicatorSettingDTO | undefined => {
    return settings.find((s) => s.timeframe === timeframe);
  };

  // Check if any operation is saving for this indicator
  const isIndicatorSaving = (indicatorId: string): boolean => {
    for (const [key] of savingStates) {
      if (key.startsWith(indicatorId)) return true;
    }
    return false;
  };

  if (roleLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-950 p-4 sm:p-6 md:p-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">
              Indicator Settings
            </h1>
            <p className="mt-1 text-gray-400">
              Enable or disable indicators per timeframe
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/admin")}
              className="rounded-md bg-gray-700 px-4 py-2 text-sm text-white hover:bg-gray-600"
            >
              Back to Admin
            </button>
            <button
              onClick={() => void fetchSettings()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-500"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 rounded-md bg-red-900/50 border border-red-700 p-4 text-red-200">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-4 text-red-400 hover:text-red-300"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Settings Grid */}
        <div className="rounded-lg border border-gray-800 bg-gray-900 overflow-hidden">
          {/* Header Row */}
          <div className="grid grid-cols-[200px_repeat(9,1fr)_100px] gap-2 border-b border-gray-800 bg-gray-850 p-4">
            <div className="text-sm font-semibold text-gray-300">Indicator</div>
            {ORDERED_TIMEFRAMES.map((tf) => (
              <div
                key={tf}
                className="text-center text-sm font-semibold text-gray-300"
              >
                {tf}
              </div>
            ))}
            <div className="text-center text-sm font-semibold text-gray-300">
              Actions
            </div>
          </div>

          {/* Indicator Rows */}
          {indicators.map((indicator) => (
            <div
              key={indicator.indicatorId}
              className="grid grid-cols-[200px_repeat(9,1fr)_100px] gap-2 border-b border-gray-800 p-4 hover:bg-gray-850 transition-colors"
            >
              {/* Indicator Label */}
              <div className="flex items-center">
                <span className="text-sm font-medium text-white">
                  {indicator.label}
                </span>
              </div>

              {/* Timeframe Toggles */}
              {ORDERED_TIMEFRAMES.map((tf) => {
                const setting = getSetting(indicator.settings, tf);
                const isEnabled = setting?.enabled ?? false;
                const key = `${indicator.indicatorId}:${tf}`;
                const isSaving = savingStates.has(key);

                return (
                  <div key={tf} className="flex items-center justify-center">
                    <button
                      onClick={() =>
                        void toggleSetting(indicator.indicatorId, tf, isEnabled)
                      }
                      disabled={isSaving}
                      className={`
                        relative h-6 w-11 rounded-full transition-colors duration-200
                        ${isEnabled ? "bg-green-600" : "bg-gray-600"}
                        ${isSaving ? "opacity-50 cursor-wait" : "cursor-pointer hover:opacity-90"}
                      `}
                      title={`${indicator.label} - ${tf}: ${isEnabled ? "Enabled" : "Disabled"}`}
                    >
                      <span
                        className={`
                          absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200
                          ${isEnabled ? "left-5" : "left-0.5"}
                        `}
                      />
                    </button>
                  </div>
                );
              })}

              {/* Quick Actions */}
              <div className="flex items-center justify-center gap-1">
                <button
                  onClick={() => void enableAll(indicator.indicatorId)}
                  disabled={isIndicatorSaving(indicator.indicatorId)}
                  className="rounded px-2 py-1 text-xs text-green-400 hover:bg-green-900/50 disabled:opacity-50"
                  title="Enable all timeframes"
                >
                  All
                </button>
                <button
                  onClick={() => void disableAll(indicator.indicatorId)}
                  disabled={isIndicatorSaving(indicator.indicatorId)}
                  className="rounded px-2 py-1 text-xs text-red-400 hover:bg-red-900/50 disabled:opacity-50"
                  title="Disable all timeframes"
                >
                  None
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="mt-6 flex items-center gap-6 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <div className="h-4 w-8 rounded-full bg-green-600" />
            <span>Enabled</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-8 rounded-full bg-gray-600" />
            <span>Disabled</span>
          </div>
        </div>

        {/* Info Note */}
        <div className="mt-4 rounded-md border border-gray-800 bg-gray-900/50 p-4 text-sm text-gray-400">
          <strong className="text-gray-300">Note:</strong> Changes take effect
          immediately on the backend. Disabled indicators will not process or
          emit signals for the specified timeframes.
        </div>
      </div>
    </div>
  );
}
